import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useCallback, useMemo } from 'react'

import { useLayoutContext } from '../../context/layoutContext'
import { useTemplateContext } from '../../context/templateContext'
import type { FPlusExpressionValue } from '../../types/templateModel'
import { isInherited } from '../../uiModels/templateOperations/types'
import type { FieldOrigin, WithOptionalOriginInfo } from '../../uiModels/types'
import { InheritedValueCopier } from '../copyInheritedValue/InheritedValueCopier'
import type { ExpressionTypeInformation } from '../fplusExpression/types'
import { TemplateFieldFPlusExpression } from './TemplateFieldFPlusExpression'

type Props = {
  expression: Optional<FPlusExpressionValue>
  isExpanded?: boolean
  isPlaceholder?: boolean
  onChange?: Optional<
    (nextValue: WithOptionalOriginInfo<FPlusExpressionValue>) => Promise<void>
  >
  path: string
  fallbackCode: string
  overflowTitle?: Optional<string>
  label?: string
  typeInformation?: Optional<ExpressionTypeInformation>
  fromOrigin?: Optional<FieldOrigin>
  isEditingInheritedValueAllowed?: boolean
}

export const TemplateFieldExpression = ({
  isExpanded,
  isPlaceholder,
  expression,
  onChange,
  path: fieldPath,
  fallbackCode,
  overflowTitle,
  label,
  typeInformation,
  fromOrigin,
  isEditingInheritedValueAllowed = true,
}: Props) => {
  const { isDraftTemplate, templateVersion } = useTemplateContext()
  const {
    preferences: {
      templates: { isShowingSimplifiedView },
    },
  } = useLayoutContext()

  const handleChangeAndSetFieldOrigin: typeof onChange = useMemo(() => {
    if (!onChange) {
      return onChange
    }

    return (nextValue: FPlusExpressionValue) =>
      onChange({ ...nextValue, origin: fieldPath })
  }, [fieldPath, onChange])

  const handleCopyInheritedValue = useCallback(
    () => onChange?.(expression ?? { fplusSource: '' }),
    [expression, onChange],
  )

  assertNotEmpty(templateVersion)

  const shouldShowOverflowFallback = !isShowingSimplifiedView && !isExpanded
  const isInheritedValue = isInherited({ path: fieldPath, origin: fromOrigin })

  const isEditableField =
    isDraftTemplate &&
    handleChangeAndSetFieldOrigin &&
    (isExpanded || isPlaceholder || isShowingSimplifiedView) &&
    (!isInheritedValue || isEditingInheritedValueAllowed)

  const isExpressionTruthy = !!expression?.fplusSource
  const isEditingBlockedForInheritedValue =
    isEditableField &&
    isInheritedValue &&
    isExpressionTruthy &&
    isEditingInheritedValueAllowed

  return (
    <div className="group relative size-full">
      <TemplateFieldFPlusExpression
        expression={expression}
        path={fieldPath}
        isEditable={!!isEditableField && !isEditingBlockedForInheritedValue}
        isShowingOverflowFallback={shouldShowOverflowFallback}
        typeInformation={typeInformation}
        label={label}
        fallbackCode={fallbackCode}
        overflowTitle={overflowTitle}
        isExpanded={isExpanded}
        onChange={handleChangeAndSetFieldOrigin}
      />
      {isEditingBlockedForInheritedValue && fromOrigin && (
        <InheritedValueCopier
          fieldPath={fieldPath}
          fromOrigin={fromOrigin}
          onCopy={handleCopyInheritedValue}
        />
      )}
    </div>
  )
}
